import { expected } from '@aloft/toolkit-shared';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { getBundleFromIndex } from './getBundleFromIndex.ts';

const PREVIOUS_TOAST_TIMESTAMP_KEY = 'ALOFT_PREVIOUS_TOAST_TIMESTAMP_KEY';
const POLLING_INTERVAL = 1000 * 30; // 30 seconds
const RENOTIFICATION_INTERVAL = 1000 * 60 * 60 * 24; // one day
const RENOTIFICATION_DELAY = 1000 * 60 * 60 * 12; // 12 hours
const NOTIFICATION_LENGTH = Infinity; // Doesn't auto dismiss, basically

export const useOutOfDatePoller = () => {
  const hasInterval = useRef(false);
  const initialScriptUrl = useMemo<string>(
    // we know this exists; wouldn't be running this code without the presence
    // of the bundle
    () => expected(getBundleFromIndex(document)),
    [],
  );
  const [currentScriptUrl, setCurrentScriptUrl] = useState<string | null | undefined>('');
  const { location } = document;
  const makeToast = useToaster();

  useEffect(() => {
    if (hasInterval.current || location.host.includes('localhost')) return;

    hasInterval.current = true;

    setInterval(() => {
      fetch(`${location.protocol}//${location.host}`)
        .then((result) => result.text())
        .then((text) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');

          // get and store the script url; this changes every time we deploy,
          //  because we upload a new bundle and then link to it from index.html
          const scriptUrl = getBundleFromIndex(doc);

          // store the one we just found, so that the useEffect below can respond
          // to it if it changes
          setCurrentScriptUrl(scriptUrl);
        })
        .catch(console.error);
    }, POLLING_INTERVAL);
  }, [location.host, location.protocol]);

  useEffect(() => {
    if (initialScriptUrl === currentScriptUrl || !(initialScriptUrl && currentScriptUrl)) return;

    // ruh roh, new script URL detected means we deployed; let's see how long it's been
    // since the last time we told this user about that

    const currentTime = Date.now();
    const storedTime = window.localStorage.getItem(PREVIOUS_TOAST_TIMESTAMP_KEY) ?? '0';
    if (currentTime - parseInt(storedTime, 10) > RENOTIFICATION_INTERVAL) {
      // yeah, it's been a while, let's tell them about it--but not immediately, in case this was a recent deploy
      setTimeout(() => {
        makeToast({
          id: 'polling-out-of-date-toast',
          type: 'info',
          title: 'Toolkit Update Available',
          body: "There's a newer version of Toolkit available. Refresh to ensure you get the latest features and things keep working as expected.",
          action: {
            text: 'Refresh',
            onClick: window.location.reload.bind(window.location),
          },
          durationInMs: NOTIFICATION_LENGTH,
        });
        window.localStorage.setItem(PREVIOUS_TOAST_TIMESTAMP_KEY, currentTime.toString());
      }, RENOTIFICATION_DELAY);
    }
  }, [currentScriptUrl, initialScriptUrl, makeToast]);
};
